import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, Badge, Empty, Loading, Tabs, InfiniteScroll } from 'antd-mobile';
import { ClockCircleOutline, PlayOutline, CheckCircleOutline, UserOutline } from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import {
  getMyAssigns,
  getScenarioDetail,
  AssignStatusEnum,
  ScenarioAssignInfo,
  ScenarioDetail
} from '../../lib/api/scenario';
import { safeToast } from '../../lib/toast';

const StyledWrapper = styled.div`
  .practice-container {
    padding: 16px;
    background: #f5f5f5;
    min-height: 100vh;
  }

  .header {
    background: white;
    padding: 20px 16px;
    margin: -16px -16px 16px -16px;
    border-bottom: 1px solid #f0f0f0;
    
    h1 {
      font-size: 1.5rem;
      font-weight: bold;
      margin: 0 0 8px 0;
      color: #1f2937;
    }
    
    p {
      font-size: 0.9rem;
      color: #666;
      margin: 0;
    }
  }

  .stats-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    
    .stat-item {
      flex: 1;
      background: white;
      padding: 12px;
      border-radius: 8px;
      text-align: center;
      
      .stat-number {
        font-size: 1.2rem;
        font-weight: bold;
        color: #1890ff;
        margin-bottom: 4px;
      }
      
      .stat-label {
        font-size: 0.8rem;
        color: #666;
      }
    }
  }

  .practice-card {
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
      
      .title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
        flex: 1;
        margin-right: 12px;
      }
    }
    
    .card-content {
      .description {
        font-size: 0.9rem;
        color: #666;
        line-height: 1.4;
        margin-bottom: 12px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      
      .meta-info {
        display: flex;
        gap: 16px;
        margin-bottom: 12px;
        
        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          color: #666;
          
          .icon {
            font-size: 0.9rem;
          }
        }
      }
      
      .tags {
        display: flex;
        gap: 6px;
        margin-bottom: 12px;
        flex-wrap: wrap;
        
        .tag {
          background: #f0f9ff;
          color: #0369a1;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
        }
      }
      
      .card-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .assign-info {
          font-size: 0.8rem;
          color: #666;
          
          .assign-time {
            color: #999;
          }
        }
        
        .action-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
        }
      }
    }
  }

  .status-badge {
    &.active {
      background: #f6ffed;
      color: #52c41a;
      border: 1px solid #b7eb8f;
    }
    
    &.cancelled {
      background: #fff2f0;
      color: #ff4d4f;
      border: 1px solid #ffccc7;
    }
  }

  .start-btn {
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    border: none;
    color: white;
    font-weight: 500;
    
    &:hover {
      background: linear-gradient(135deg, #096dd9 0%, #0050b3 100%);
    }
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    
    .empty-icon {
      font-size: 3rem;
      color: #d9d9d9;
      margin-bottom: 16px;
    }
    
    .empty-title {
      font-size: 1.1rem;
      color: #666;
      margin-bottom: 8px;
    }
    
    .empty-desc {
      font-size: 0.9rem;
      color: #999;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
  }
`;

const AssignedPracticeList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assigns, setAssigns] = useState<ScenarioAssignInfo[]>([]);
  const [activeTab, setActiveTab] = useState('active');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    cancelled: 0
  });

  const loadAssigns = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setPage(1);
        setAssigns([]);
      }
      
      const currentPage = isRefresh ? 1 : page;
      const status = activeTab === 'all' ? undefined : (activeTab as AssignStatusEnum);
      
      const response = await getMyAssigns({
        status,
        page: currentPage,
        pageSize: 10
      });

      if (response.success && response.data) {
        const newAssigns = response.data.assigns || response.data.list || [];
        
        if (isRefresh) {
          setAssigns(newAssigns);
        } else {
          setAssigns(prev => [...prev, ...newAssigns]);
        }
        
        setHasMore(newAssigns.length === 10);
        setPage(prev => isRefresh ? 2 : prev + 1);
        
        // 更新统计信息
        if (isRefresh || currentPage === 1) {
          setStats({
            total: response.data.total,
            active: newAssigns.filter(a => a.status === AssignStatusEnum.ACTIVE).length,
            cancelled: newAssigns.filter(a => a.status === AssignStatusEnum.CANCELLED).length
          });
        }
      } else {
        safeToast.error(response.errorMessage || '获取指派列表失败');
      }
    } catch (error) {
      console.error('获取指派列表失败:', error);
      safeToast.error('获取指派列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssigns(true);
  }, [activeTab]);

  const handleStartPractice = (assign: ScenarioAssignInfo) => {
    // 跳转到具体的陪练页面
    navigate(`/practice/${assign.scenario_id}`, {
      state: { assignId: assign.id }
    });
  };

  const handleViewDetail = (assign: ScenarioAssignInfo) => {
    // 跳转到场景详情页面
    navigate(`/learn/scenario/${assign.scenario_id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: AssignStatusEnum) => {
    const statusMap = {
      [AssignStatusEnum.ACTIVE]: { text: '进行中', className: 'active' },
      [AssignStatusEnum.CANCELLED]: { text: '已取消', className: 'cancelled' }
    };
    
    const statusInfo = statusMap[status];
    return (
      <Badge 
        content={statusInfo.text} 
        className={`status-badge ${statusInfo.className}`}
      />
    );
  };

  const tabs = [
    { key: 'active', title: `进行中 (${stats.active})` },
    { key: 'cancelled', title: `已取消 (${stats.cancelled})` },
    { key: 'all', title: `全部 (${stats.total})` }
  ];

  if (loading && assigns.length === 0) {
    return (
      <StyledWrapper>
        <div className="loading-container">
          <Loading />
        </div>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper>
      <div className="practice-container">
        <div className="header">
          <h1>我的陪练</h1>
          <p>查看指派给我的陪练场景</p>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">总计</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">进行中</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{stats.cancelled}</div>
            <div className="stat-label">已取消</div>
          </div>
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          style={{ marginBottom: '16px' }}
        >
          {tabs.map(tab => (
            <Tabs.Tab key={tab.key} title={tab.title} />
          ))}
        </Tabs>

        {assigns.length === 0 ? (
          <div className="empty-state">
            <UserOutline className="empty-icon" />
            <div className="empty-title">暂无陪练指派</div>
            <div className="empty-desc">
              {activeTab === 'active' ? '当前没有进行中的陪练' : 
               activeTab === 'cancelled' ? '没有已取消的陪练' : '还没有任何陪练指派'}
            </div>
          </div>
        ) : (
          <>
            {assigns.map((assign) => (
              <Card key={assign.id} className="practice-card">
                <div className="card-header">
                  <h3 className="title">{assign.scenario_name}</h3>
                  {getStatusBadge(assign.status)}
                </div>
                
                <div className="card-content">
                  {assign.assign_description && (
                    <p className="description">{assign.assign_description}</p>
                  )}
                  
                  <div className="meta-info">
                    <div className="meta-item">
                      <UserOutline className="icon" />
                      <span>指派人: {assign.assigned_by.name}</span>
                    </div>
                    <div className="meta-item">
                      <ClockCircleOutline className="icon" />
                      <span>{formatDate(assign.assigned_at)}</span>
                    </div>
                  </div>
                  
                  {assign.effective_start_time && assign.effective_end_time && (
                    <div className="meta-info">
                      <div className="meta-item">
                        <span>有效期: {formatDate(assign.effective_start_time)} - {formatDate(assign.effective_end_time)}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="card-footer">
                    <div className="assign-info">
                      <div className="assign-time">
                        指派时间: {formatDate(assign.assigned_at)}
                      </div>
                    </div>
                    
                    <div className="action-buttons">
                      <Button 
                        size="small" 
                        fill="outline"
                        onClick={() => handleViewDetail(assign)}
                        style={{ marginRight: '8px' }}
                      >
                        查看详情
                      </Button>
                      {assign.status === AssignStatusEnum.ACTIVE && (
                        <Button 
                          size="small" 
                          color="primary"
                          onClick={() => handleStartPractice(assign)}
                        >
                          <PlayOutline /> 开始陪练
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            
            <InfiniteScroll 
              loadMore={() => loadAssigns(false)} 
              hasMore={hasMore}
            />
          </>
        )}
      </div>
    </StyledWrapper>
  );
};

export default AssignedPracticeList;